import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LetDirective, PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { BehaviorSubject, switchMap } from 'rxjs';

import { selectCurrentUserId, selectUserAvatarInfo } from '@ninety/platform/state';
import { TerraIconModule, TerraTextInputModule } from '@ninety/terra';
import { ButtonComponent } from '@ninety/ui/core';

import { GuideActions } from '../../../guide/_state/guide.actions';
import { mazChatAnimation } from '../../../guide/components/getting-started-guide/getting-started-guide.animation';
import { MazActions } from '../../_state/maz.actions';
import { MazSelectors } from '../../_state/maz.selectors';
import { MazFeedbackFlag } from '../../models/maz-feedback-flag.enum';
import { MazMessage } from '../../models/maz-message';
import { MazChatToasterComponent } from '../maz-chat-toaster/maz-chat-toaster.component';
import { MazCommonQuestionsComponent } from '../maz-common-questions/maz-common-questions.component';
import { MazMessageComponent } from '../maz-message/maz-message.component';
import { MazWaitingForResponseComponent } from '../maz-waiting-for-response/maz-waiting-for-response.component';

import { ChatWindowHeightDirective } from './chat-window-height.directive';

@Component({
  selector: 'ninety-maz-chat',
  standalone: true,
  imports: [
    LetDirective,
    PushPipe,
    FormsModule,
    TerraTextInputModule,
    ButtonComponent,
    ChatWindowHeightDirective,
    MazCommonQuestionsComponent,
    MazMessageComponent,
    MazWaitingForResponseComponent,
    MazChatToasterComponent,
    TerraIconModule,
  ],
  animations: [mazChatAnimation],
  templateUrl: './maz-chat.component.html',
  styleUrls: ['./maz-chat.component.scss'],
})
export class MazChatComponent {
  private store = inject(Store);

  protected readonly description$ = this.store.select(MazSelectors.selectDescription);
  protected readonly isMazChatExpanded$ = this.store.select(MazSelectors.selectIsMazChatExpanded);
  protected readonly mazMessages$ = this.store.select(MazSelectors.selectConversationHistory);
  protected readonly waitingForMazResponse$ = this.store.select(MazSelectors.selectWaitingForMazResponse);
  protected readonly showMazAlert$ = new BehaviorSubject<boolean>(false);
  protected readonly mazToasterFlag$ = new BehaviorSubject<MazFeedbackFlag>(null);

  // handing this to all messages to every message doesn't need to import the store and get the user's picture.url
  protected readonly userAvatarInfo$ = this.store
    .select(selectCurrentUserId)
    .pipe(switchMap(userId => this.store.select(selectUserAvatarInfo(userId))));

  mazInput: string;

  toggleChatWindow() {
    this.store.dispatch(MazActions.toggleMazChat());
  }

  sendMessage(): void {
    this.store.dispatch(MazActions.sendMessageToMaz({ userInput: this.mazInput }));
    this.mazInput = null;
  }

  toggleMazFeedbackFlag({ feedback, _id: messageId }: MazMessage): void {
    const flag = feedback?.flag === MazFeedbackFlag.incorrect ? MazFeedbackFlag.correct : MazFeedbackFlag.incorrect;
    this.store.dispatch(MazActions.sendMessageFeedback({ feedback: { ...feedback, messageId, flag } }));
    this.mazToasterFlag$.next(flag);
    if (flag === MazFeedbackFlag.correct) this.showMazAlert();
  }

  submitFeedback(text: string, { feedback, _id: messageId }: MazMessage): void {
    this.store.dispatch(MazActions.sendMessageFeedback({ feedback: { ...feedback, messageId, text } }));
    this.showMazAlert();
  }

  showMazAlert(): void {
    this.showMazAlert$.next(true);
  }

  hideMazAlert(): void {
    this.showMazAlert$.next(false);
  }

  closeMazAndOpenIntercom(): void {
    this.store.dispatch(GuideActions.closeMazAndOpenIntercom());
  }
}
